import React from "react";
import { useTableHooks } from "../../../../atoms/table/useTableHooks";
import { hasPermission, pascalToSnakeCase } from "../../../../../utils";
import { Pagination } from "../../../../molecules/pagination";
import { Dropdown } from "../../../../atoms/dropdown";
import { Divider } from "../../../../atoms/divider";
import { Table } from "../../../../atoms/table";
import { Input } from "../../../../atoms/input";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { usePaginationHook } from "../../../../molecules/pagination/usePaginationHook";
import { PAGE_NAME, PERMISSIONS, ROUTE } from "../../../../../constants/constants";
import { useBoxCollaboratorHistoryQuery } from "../../../../../store/box/boxSlice";
import { Icon } from "../../../../atoms/icon";
import { CustomDatePicker } from "../../../../atoms/datePicker";
import { SynchronizeBoxCollaborator } from "../../../../organisms/boxCollaborator";

const toolSortHandler = (sortBy: string) => {
  switch (sortBy) {
    default:
      return sortBy;
  }
};

const BoxCollaboratorHistory = () => {
  const pageClassName = "p-boxCollaboratorHistory";

  const filterRef = React.useRef<HTMLDivElement>(null);
  const [isShowModal, setIsShowModal] = React.useState(false);
  const [isShowFilter, setShowFilter] = React.useState(false);
  const [filter, setFilter] = React.useState({
    startDate: "",
    endDate: "",
  });

  const [filterQuery, setFilterQuery] = React.useState({
    startDate: "",
    endDate: "",
  });

  const {
    search,
    query,
    pageChangeHandler,
    sortHandler,
    limitChangeHandler,
    searchHandler,
  } = useTableHooks();

  const { data, isLoading, isFetching } = useBoxCollaboratorHistoryQuery({
    search: query.search,
    limit: query.limit,
    current_page: query.current,
    sort_by: pascalToSnakeCase(toolSortHandler(query.sortBy)),
    sort_direction: query.sortDirection,
    start_date: filterQuery.startDate,
    end_date: filterQuery.endDate
  });

  const tableHeader = [
    {
      title: "Serial",
      dataIndex: "id",
      isSortable: true,
    },
    {
      title: "Collaborator Name",
      dataIndex: "name",
      isSortable: true,
    },
    {
      title: "Collaborator Email",
      dataIndex: "email",
      isSortable: true,
    },
    {
      title: "Last Action",
      dataIndex: "action",
      isSortable: false,
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      isSortable: true,
    },
    {
      title: "Parent Folder",
      dataIndex: "parentFolder",
      isSortable: true,
    },
    {
      title: "Last Activity",
      dataIndex: "activityDate",
      isSortable: true,
    },
  ];

  const {
    totalData,
    handleFirstPage,
    handlePrevPage,
    handleLastPage,
    handleNextPage,
    renderPageNumbers,
  } = usePaginationHook({
    totalNumberOfData: data?.result?.total,
    perPage: query.limit,
    setCurrentPage: pageChangeHandler,
    currentPage: query.current,
    className: "m-pagination",
  });

  const formattedSortBy = (sortBy: string) => {
    switch (sortBy) {
      case "first_name":
        return "userFirstName";
      case "last_name":
        return "userLastName";
      case "email":
        return "userEmail";
      default:
        return sortBy;
    }
  };

  const resetFilter = () => {
    setFilter({
      startDate: "",
      endDate: "",
    });

    handleFirstPage();
  };

  const filterHandler = () => {
    setFilterQuery({
      ...filterQuery,
      startDate: filter.startDate,
      endDate: filter.endDate,
    });
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setShowFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={pageClassName}>
      <Breadcrumb />
      <p className={`${pageClassName}__title`}>
        {PAGE_NAME.BOX_COLLABORATOR_HISTORY}
      </p>
      <div className={`${pageClassName}__container`}>
        <div className={`${pageClassName}__header`}>
          <div className={`${pageClassName}__search`}>
            <Input
              placeholder="Search"
              onChange={(e) => {
                searchHandler(e);
                handleFirstPage();
              }}
              value={search}
            />
          </div>

          <div className={`${pageClassName}__filterBy`}>
            {(filterQuery.startDate || filterQuery.endDate) && (
              <div className={`${pageClassName}__filterBy__item`}>
                <p>
                  {filterQuery.startDate} - {filterQuery.endDate}
                </p>
              </div>
            )}
          </div>

          <div className={`${pageClassName}__filter`} ref={filterRef}>
            {hasPermission(PERMISSIONS.SYNC_SAAS_USER.title) && (
              <button
                onClick={() => setIsShowModal(true)}
                className={`${pageClassName}__filter__button ${pageClassName}__filter__button--synchronize`}
                title="Synchronize"
              >
                Synchronize Data
              </button>
            )}
            <button
              onClick={() => setShowFilter(!isShowFilter)}
              className={`${pageClassName}__filter__button`}
              title="Filter"
            >
              <Icon icon="filter" size="primary" />
            </button>

            {isShowFilter && (
              <div className={`${pageClassName}__filter__container`}>
                <div className={`${pageClassName}__filter__item`}>
                  <label>Start Date</label>
                  <CustomDatePicker
                    value={
                      filter.startDate ? new Date(filter.startDate) : undefined
                    }
                    onChange={(date) =>
                      setFilter({
                        ...filter,
                        startDate: date?.toString() || "",
                      })
                    }
                    max={filter.endDate ? new Date(filter.endDate) : new Date()}
                  />
                </div>
                <div className={`${pageClassName}__filter__item`}>
                  <label>End Date</label>
                  <CustomDatePicker
                    value={
                      filter.endDate ? new Date(filter.endDate) : undefined
                    }
                    onChange={(date) => {
                      setFilter({ ...filter, endDate: date?.toString() || "" });
                    }}
                    min={
                      filter.startDate ? new Date(filter.startDate) : undefined
                    }
                    max={new Date()}
                  />
                </div>
                <Divider />
                <div className={`${pageClassName}__filter__btn`}>
                  <button onClick={resetFilter}>Reset</button>
                  <button
                    onClick={() => {
                      setShowFilter(false);
                      filterHandler();
                      handleFirstPage();
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={`${pageClassName}__table`}>
          <Table
            header={tableHeader}
            data={data?.result?.data ?? []}
            loading={isFetching}
            firstLoad={isLoading}
            sortHandler={sortHandler}
            minWidth="1440"
            startIndex={data?.result?.from}
            sortBy={formattedSortBy(query.sortBy)}
            viewURL={ROUTE.BOX_COLLABORATOR_HISTORY}
            cursorPointer
          />
        </div>
        <Divider />

        <div className={`${pageClassName}__pagination`}>
          <div className={`${pageClassName}__pagination__limit`}>
            <p>Display</p>
            <div className={`${pageClassName}__pagination__dropdown`}>
              <Dropdown
                options={[
                  { label: "20", value: 20 },
                  { label: "50", value: 50 },
                  { label: "100", value: 100 },
                ]}
                selected={{ label: query.limit, value: query.limit }}
                onClickChange={(e) => {
                  limitChangeHandler(e.value as number);
                  handleFirstPage();
                }}
                isBordered
                isIcon
              />
            </div>
          </div>
          <div className={`${pageClassName}__pagination__number`}>
            {data?.result?.total ? (
              <p>
                Show {data?.result?.from} -{" "}
                {data?.result?.from + query.limit - 1 < data?.result?.total
                  ? data?.result?.from + query.limit - 1
                  : data?.result?.total}{" "}
                of {data?.result?.total}
              </p>
            ) : (
              <p>Show 00 - 00 of 00</p>
            )}
          </div>
          <div className={`${pageClassName}__pagination__nav`}>
            {data?.result?.total > query.limit && (
              <Pagination
                totalData={totalData}
                page={query.current}
                handleFirstPage={handleFirstPage}
                handlePrevPage={handlePrevPage}
                handleLastPage={handleLastPage}
                handleNextPage={handleNextPage}
                renderPageNumbers={renderPageNumbers}
              />
            )}
          </div>
        </div>
      </div>

      <SynchronizeBoxCollaborator
        isOpen={isShowModal}
        onClosed={() => setIsShowModal(false)}
      />
    </div>
  );
};

export default BoxCollaboratorHistory;
